import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import FieldLabel from './FieldLabel';

dayjs.extend(customParseFormat);

const CANONICAL = 'HH:mm';

/**
 * Shown beneath the input whenever the caller has no error or helper text of its own.
 * The field only accepts 24-hour input, so say so rather than leaving the biller to infer
 * it from the "HH:mm" placeholder — an anesthesia window entered as 2:30 meaning 14:30
 * would be wrong by twelve hours and look plausible.
 */
const MILITARY_TIME_HINT = 'Use military time (e.g. 14:30)';

interface LabeledTimePickerProps {
  label: string;
  /** Canonical 'HH:mm' (24-hour) string; '' when unset. */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  asteriskColor?: string;
  disabled?: boolean;
  /**
   * Show the standing military-time hint when there is no error or caller message.
   * The back office turns it off: its claim card is narrow and repeats this control
   * four times, where four copies of the same sentence is noise.
   *
   * With the hint off and nothing else to say, no helper-text element renders at all,
   * so the field reclaims that line rather than leaving it blank.
   */
  showHint?: boolean;
}

/**
 * FieldLabel + MUI TimePicker — canonical 24-hour 'HH:mm' in/out, so times stay comparable.
 * The colon is structural: the field is segmented into HH and mm, so typing "1430" lands
 * as 14:30 without the user entering a separator. Mirrors LabeledDatePicker.
 */
export default function LabeledTimePicker({
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  asteriskColor,
  disabled,
  showHint = true,
}: LabeledTimePickerProps) {
  // The picker owns the in-progress value; only completed times are published upward.
  // Deriving it from `value` instead cannot work — see LabeledDatePicker for the full
  // reasoning. Here it meant that clearing the minutes of 14:30 also took the hour.
  const [dayjsValue, setDayjsValue] = useState<Dayjs | null>(() => {
    if (!value) return null;
    const d = dayjs(value, CANONICAL, true);
    return d.isValid() ? d : null;
  });
  // What was last sent up, so the effect below can tell a real external change (form
  // reset, record load) from the echo of our own emit arriving back as a prop.
  const lastPublishedRef = useRef(value);

  useEffect(() => {
    if (value === lastPublishedRef.current) return;
    lastPublishedRef.current = value;
    const d = value ? dayjs(value, CANONICAL, true) : null;
    setDayjsValue(d && d.isValid() ? d : null);
  }, [value]);

  const handleChange = (val: Dayjs | null) => {
    setDayjsValue(val);
    const canonical = val && val.isValid() ? val.format(CANONICAL) : '';
    lastPublishedRef.current = canonical;
    onChange(canonical);
  };

  return (
    <Box>
      <FieldLabel required={required} asteriskColor={asteriskColor}>
        {label}
      </FieldLabel>
      <TimePicker
        value={dayjsValue}
        onChange={handleChange}
        format={CANONICAL}
        ampm={false}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            error,
            // The caller's message (e.g. a cross-field validation error) wins over the
            // standing hint. With the hint off and nothing to say this is `undefined`
            // rather than '': a blank string still renders the helper-text element,
            // leaving an empty line under the field.
            helperText: helperText || (showHint ? MILITARY_TIME_HINT : undefined),
            inputProps: { 'aria-label': label },
          },
        }}
      />
    </Box>
  );
}
